"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/* Waypoints in a React hook!
 *  const watchWhenUserReachesOneThousandPX = useWaypoint({ waypoint: 1000 })
 *  const watchWhenUserReachesHerpDerp = useWaypoint({ waypointSelector: '#herpderp', offset: -200 })
 *
 *  usage: waypointSelector should be an ID.
 *    // I wonder if I should just use `waypointSelector: 'herpderp'` and
 *    //  getElementById to prevent someone feeding a class or a (shudder) tag
 *
 *  Interval is a number;
 *    1 is low sensitivity (every 1000 milliseconds),
 *    1000 is once a millisecond (actually, once every frame, which is maybe 60 milliseconds I think?)
 *
 *  Possible future optimization:
 *    If a user were to assign numerous waypoints, each would be running a window.onScroll listener.
 *    Those are expensive.
 *    We could potentially share scrollPosition using a React Context.
 *    https://www.codementor.io/@sambhavgore/an-example-use-context-and-hooks-to-share-state-between-different-components-sgop6lnrd
 */
const react_1 = __importDefault(require("react"));
const _utils_1 = require("@utils");
function getScrollPosition() {
    if (typeof window === 'undefined') {
        return {
            x: 0,
            y: 0
        };
    }
    return {
        x: window.pageXOffset,
        y: window.pageYOffset
    };
}
function getY(element) {
    var yPosition = 0;
    while (element) {
        yPosition += element.offsetTop - element.scrollTop + element.clientTop;
        element = element.offsetParent;
    }
    return yPosition;
}
function useWaypoint(_ref) {
    var _ref$sensitivity = _ref.sensitivity, sensitivity = _ref$sensitivity === void 0 ? 100 : _ref$sensitivity, _ref$waypoint = _ref.waypoint, waypoint = _ref$waypoint === void 0 ? 50 : _ref$waypoint, waypointSelector = _ref.waypointSelector, _ref$offset = _ref.offset, offset = _ref$offset === void 0 ? 0 : _ref$offset;
    var _React$useState = react_1.default.useState(false), reachedWaypoint = _React$useState[0], setReachedWaypoint = _React$useState[1];
    var _React$useState2 = react_1.default.useState(waypoint), waypointPosition = _React$useState2[0], setWaypointPosition = _React$useState2[1]; // If the width of the screen changes, the layout reflow will most likely move the waypoint.
    // Watch for screen changes here and adapt accordingly.
    // We don't need this if waypoint is a predetermined number.
    var _useWindowSize = _utils_1.useWindowSize(), width = _useWindowSize.width;
    react_1.default.useEffect(function () {
        if (!waypointSelector)
            return;
        setWaypointPosition(getY(document.querySelector(waypointSelector)));
    }, [width]);
    var fpsInterval = 1000 / sensitivity;
    react_1.default.useEffect(function () {
        if (typeof window === 'undefined')
            return;
        var requestRunning = null;
        var then = Date.now();
        var handleScroll = function handleScroll() {
            if (requestRunning) {
                window.cancelAnimationFrame(requestRunning);
            }
            requestRunning = window.requestAnimationFrame(function () {
                var now = Date.now();
                var elapsed = now - then;
                if (elapsed > fpsInterval) {
                    then = now - elapsed % fpsInterval;
                    var newWaypointReached = getScrollPosition().y >= waypointPosition + offset;
                    if (reachedWaypoint === newWaypointReached)
                        return;
                    setReachedWaypoint(newWaypointReached);
                }
            });
        };
        window.addEventListener('scroll', handleScroll);
        return function () {
            return window.removeEventListener('scroll', handleScroll);
        };
    }, [waypointPosition, reachedWaypoint, offset]);
    return reachedWaypoint;
}
exports.default = useWaypoint;
